import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Home, Loader2, CalendarDays, Newspaper, ArrowLeft, Eye } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import api, { mediaUrl } from "@/lib/api";
import ArticleBody from "@/components/ArticleBody";
import { VideoThumb } from "@/components/VideoThumb";
import { ShareButtons } from "@/components/ShareButtons";
import { PublicNewsCard } from "@/components/PublicNewsCard";
import { ViewsChart } from "@/components/ViewsChart";
import { getVideoEmbed as videoEmbed } from "@/components/SectionBlocks";

export default function NewsDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/news/${id}`).then((r) => setItem(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
    api.post(`/news/${id}/view`).catch(() => {});
  }, [id]);

  // Correlati: prima quelle della stessa disciplina della notizia aperta
  useEffect(() => {
    if (!item) return;
    api.get("/news").then((r) => {
      const all = (r.data || []).filter((n) => n.id !== item.id && n.published !== false);
      const same = all.filter((n) => item.discipline && n.discipline === item.discipline);
      const others = all.filter((n) => !(item.discipline && n.discipline === item.discipline));
      setRelated([...same, ...others].slice(0, 3));
    }).catch(() => {});
  }, [item]);

  const hasVideo = item?.video_url && videoEmbed(item.video_url);

  return (
    <div className="min-h-screen bg-background text-foreground noise-bg overflow-x-hidden">
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 min-w-0">
            <SiteLogo className="w-9 h-9 shrink-0" />
            <div className="flex flex-col leading-tight min-w-0 truncate"><BrandText /></div>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5 shrink-0"><Home className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
      ) : notFound || !item ? (
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="text-muted-foreground">Notizia non trovata.</p>
          <Link to="/" className="text-gold hover:underline mt-4 inline-block">Torna alla home</Link>
        </div>
      ) : (
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="news-detail">
          <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-gold border border-gold/40 rounded-full px-3 py-1"><Newspaper className="w-3 h-3" /> Notizia</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4 break-words">{item.title}</h1>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pb-6 border-b border-white/10">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><CalendarDays className="w-4 h-4 text-gold" />{new Date(item.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</span>
              {isAdmin && <span data-testid="news-views" className="flex items-center gap-1.5 text-sm text-muted-foreground"><Eye className="w-4 h-4 text-gold" />{item.views || 0} visualizzazioni</span>}
            </div>
            <ShareButtons title={item.title} size="lg" />
          </div>

          {(() => {
            const imgs = (item.images && item.images.length) ? item.images : (item.image_url ? [item.image_url] : []);
            if (!imgs.length) return null;
            return (
              <div className="mt-6 space-y-4" data-testid="news-detail-images">
                <div className="w-full aspect-video rounded-xl border border-white/10 overflow-hidden">
                  <img src={mediaUrl(imgs[0])} alt={item.title} className="w-full h-full object-cover" style={{ objectPosition: item.image_position || "50% 50%" }} />
                </div>
                {imgs.slice(1).map((u, i) => (
                  <div key={i} className="w-full rounded-xl border border-white/10 overflow-hidden bg-[#0B1220]">
                    <img src={mediaUrl(u)} alt={`${item.title} ${i + 2}`} className="w-full h-auto object-contain" />
                  </div>
                ))}
              </div>
            );
          })()}

          <ArticleBody text={item.body} className="text-muted-foreground mt-6 text-base leading-relaxed" showNewsletterCta />

          {isAdmin && <div className="mt-8"><ViewsChart viewsByDay={item.views_by_day} total={item.views} /></div>}

          {hasVideo && (
            <div className="mt-8" data-testid="news-detail-video">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><span className="w-1.5 h-5 bg-gold rounded-full" /> Video</h2>
              <VideoThumb url={item.video_url} poster={item.image_url ? mediaUrl(item.image_url) : null} />
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between gap-4">
            <Link to="/" className="text-sm text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> Torna alla home</Link>
            <ShareButtons title={item.title} />
          </div>

          {related.length > 0 && (
            <section className="mt-14" data-testid="related-news">
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight mb-6">Altre notizie</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((n) => <PublicNewsCard key={n.id} item={n} />)}
              </div>
            </section>
          )}
        </article>
      )}
    </div>
  );
}
